import { Link, useNavigate } from "react-router-dom";
import { Instagram, Facebook, Phone, Mail, MapPin } from "lucide-react";
import { BRAND } from "@/constants/branding";

const colLinks = [
  {
    title: "Shop",
    links: [
      { label: "All Products", to: "/products" },
      { label: "Categories", to: "/categories" },
      { label: "Featured Deals", to: "/products?featured=1" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", to: "/about" },
      { label: "Contact Us", to: "/contact" },
      { label: "Help & Support", to: "/help" },
      { label: "Become a Vendor", to: "/register?role=vendor" },
    ],
  },
  {
    title: "Policies",
    links: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms & Conditions", to: "/terms" },
      { label: "Shipping Policy", to: "/shipping" },
      { label: "Refund & Return Policy", to: "/refund" },
    ],
  },
];

export default function Footer() {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-deep text-white/75 mt-10 sm:mt-14" data-testid="site-footer">
      <div className="h-1 bg-gradient-to-r from-primary via-[#e9c46a] to-primary" />
      <div className="shell py-12 sm:py-14">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <button
              onClick={() => navigate("/")}
              className="bg-white rounded-xl px-3 py-2 inline-block shadow-lg"
              data-testid="footer-logo"
            >
              <img src={BRAND.logo} alt="Zilkart" className="h-9 w-auto object-contain" />
            </button>
            <p className="mt-5 text-sm leading-relaxed max-w-sm text-white/65">
              {BRAND.name} is a modern multi-vendor marketplace. {BRAND.tagline}.
            </p>
            <div className="mt-5 space-y-2 text-sm">
              <a href={`tel:${BRAND.phone}`} className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-phone">
                <Phone className="h-4 w-4 text-[#e9c46a]" /> {BRAND.phone}
              </a>
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 hover:text-white transition-colors break-all" data-testid="footer-email">
                <Mail className="h-4 w-4 text-[#e9c46a]" /> {BRAND.email}
              </a>
              <span className="flex items-center gap-2 text-white/65">
                <MapPin className="h-4 w-4 text-[#e9c46a]" /> India
              </span>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <a
                href={BRAND.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-primary flex items-center justify-center transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href={BRAND.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-primary flex items-center justify-center transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>

          {colLinks.map((col) => (
            <div key={col.title}>
              <h4 className="text-[#e9c46a] font-bold mb-4 text-xs uppercase tracking-[0.2em]">{col.title}</h4>
              <ul className="space-y-2.5 text-sm">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="text-white/65 hover:text-white transition-colors"
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/50">
          <p>© {year} {BRAND.name} Technologies · {BRAND.tagline}</p>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
